import type { ApiClient } from '@adiox/api';
import type { PrefsData, PrefsStore } from './prefsStore';

/**
 * Keeps the local preferences and the server copy in step.
 *
 * - On start the server copy is loaded. If the server has none yet (revision 0), the local cache (what the user set before,
 *   or on this device) is uploaded; otherwise the server copy wins, so the same layout follows the user to every device.
 * - Changes are saved after a short pause, based on the revision they were edited from.
 * - If another tab or device saved in the meantime the server answers 409: the newer server data is loaded and the keys
 *   changed here are laid on top of it, then saved again. Nothing either side changed is lost.
 */
export class PrefsSyncer {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribe: (() => void) | null = null;
  private saving: Promise<void> | null = null;
  private stopped = false;

  constructor(
    private readonly store: PrefsStore,
    private readonly api: ApiClient,
    private readonly options: { debounceMs?: number } = {},
  ) {}

  async start(): Promise<void> {
    this.stopped = false;
    this.store.setState({ status: 'loading' });
    try {
      const { data } = await this.api.GET('/api/v1/me/preferences');
      if (!data || this.stopped) throw new Error('preferences unavailable');
      const local = this.store.getState().data;
      const server = data.data as PrefsData;
      if (data.revision === 0 && Object.keys(local).length > 0) {
        this.store.setState({ revision: 0, status: 'synced', dirty: Object.keys(local) });
        await this.flush(); // first device: upload what was set locally
      } else {
        this.store.hydrate(server, data.revision);
      }
    } catch {
      this.store.setState({ status: 'error' }); // keep working from the local cache; saving retries
    }
    this.unsubscribe = this.store.subscribe((state, previous) => {
      if (state.dirty.length > 0 && state.data !== previous.data) this.schedule();
    });
  }

  stop(): void {
    this.stopped = true;
    this.unsubscribe?.();
    if (this.timer) clearTimeout(this.timer);
  }

  /** Saves now (used on sign-out and page hide). */
  async flush(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.saving = (this.saving ?? Promise.resolve()).then(() => this.save());
    await this.saving;
  }

  private schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.options.debounceMs ?? 1_500);
  }

  private async save(): Promise<void> {
    for (let attempt = 0; attempt < 3; attempt++) {
      const state = this.store.getState();
      if (state.dirty.length === 0) return;
      this.store.setState({ status: 'saving' });
      let result;
      try {
        result = await this.api.PUT('/api/v1/me/preferences', {
          body: { data: state.data, baseRevision: state.revision },
        });
      } catch {
        this.store.setState({ status: 'error' });
        return;
      }
      if (result.response.status === 409) {
        await this.mergeFromServer(state.dirty);
        continue;
      }
      if (!result.data) {
        this.store.setState({ status: 'error' });
        return;
      }
      // Anything changed while the request was in flight stays dirty for the next save.
      const now = this.store.getState();
      const changedSince =
        now.data === state.data ? [] : now.dirty.filter((k) => now.data[k] !== state.data[k]);
      this.store.setState({ revision: result.data.revision, dirty: changedSince, status: 'synced' });
      if (changedSince.length > 0) this.schedule();
      return;
    }
    this.store.setState({ status: 'error' });
  }

  /** Another device saved first: take its data and lay our own changed keys on top. */
  private async mergeFromServer(myKeys: string[]): Promise<void> {
    const { data } = await this.api.GET('/api/v1/me/preferences');
    if (!data) return;
    const mine = this.store.getState().data;
    const merged: PrefsData = { ...(data.data as PrefsData) };
    for (const key of myKeys) merged[key] = mine[key];
    this.store.setState({ data: merged, revision: data.revision, dirty: myKeys });
  }
}
